import {
  ChangeDetectionStrategy,
  Component,
  computed,
  EventEmitter,
  input,
  OnInit,
  Output,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { switchMap } from 'rxjs';
import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { MatTableModule } from '@angular/material/table';
import { MatSortModule, Sort, SortDirection } from '@angular/material/sort';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { ITableDataSource } from '../ITableDataSource';
import { TableColumn } from '../TableControls';
import { TableStateManagement } from '../../selection-dialog/table-state-management';
import { SkeletonTableLoaderComponent } from '../../skeleton-table-loader/skeleton-table-loader';

@Component({
  selector: 'lib-generic-table',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AsyncPipe,
    NgTemplateOutlet,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    MatCheckboxModule,
    MatProgressBarModule,
    SkeletonTableLoaderComponent,
  ],
  templateUrl: './generic-table.component.html',
  styleUrl: './generic-table.component.scss',
})
export class GenericTableComponent<T, F = Record<string, unknown>> implements OnInit {
  readonly dataSource = input.required<ITableDataSource<T, F>>();
  readonly columns = input.required<TableColumn[]>();
  readonly selectionMode = input<'none' | 'single' | 'multi'>('none');
  readonly identityFn = input<((item: T) => string | number) | undefined>(undefined);
  readonly defaultSort = input<string>('');
  readonly defaultSortDirection = input<SortDirection>('asc');
  readonly pageSize = input<number>(20);
  readonly pageSizeOptions = input<number[]>([10, 20, 50]);
  readonly rowClickable = input<boolean>(true);
  readonly stickyHeader = input<boolean>(true);
  readonly showPaginator = input<boolean>(true);
  readonly noDataMessage = input<string>('No records found.');

  @Output() readonly rowClicked = new EventEmitter<T>();
  @Output() readonly rowDoubleClicked = new EventEmitter<T>();
  @Output() readonly selectionChanged = new EventEmitter<T[]>();

  protected readonly displayedColumns = computed(() => {
    const cols = this.columns()
      .filter(c => !c.isDisabled && c.isVisible !== false)
      .map(c => c.memberName);
    return this.selectionMode() !== 'none' ? ['select', ...cols] : cols;
  });

  // Reactive current items — avoids using async pipe inside event handlers
  protected readonly currentItems = toSignal(
    toObservable(this.dataSource).pipe(switchMap(ds => ds.items$)),
    { initialValue: [] as T[] },
  );

  protected tableState: TableStateManagement<T> | null = null;

  ngOnInit(): void {
    const mode = this.selectionMode();
    const fn = this.identityFn();
    if (mode !== 'none' && fn) {
      this.tableState = new TableStateManagement<T>(fn, mode);
    }
  }

  protected onSort(sort: Sort): void {
    this.dataSource().setPageSort(sort.active, sort.direction);
  }

  protected onPage(event: PageEvent): void {
    const ds = this.dataSource();
    ds.setPaging(ds.sort, ds.sortDirection, event.pageIndex, event.pageSize);
  }

  protected onRowClick(row: T): void {
    if (this.tableState) {
      this.tableState.toggle(row);
      this.selectionChanged.emit(this.tableState.getSelected());
    }
    this.rowClicked.emit(row);
  }

  protected onRowDoubleClick(row: T): void {
    this.rowDoubleClicked.emit(row);
  }

  protected onCheckboxChange(row: T): void {
    if (!this.tableState) return;
    this.tableState.toggle(row);
    this.selectionChanged.emit(this.tableState.getSelected());
  }

  protected onMasterCheckboxChange(): void {
    if (!this.tableState) return;
    const items = this.currentItems();
    const allSelected = this.tableState.isAllSelected(items);
    this.tableState.toggleAll(items, !allSelected);
    this.selectionChanged.emit(this.tableState.getSelected());
  }

  protected isSelected(row: T): boolean {
    return this.tableState?.isSelected(row) ?? false;
  }

  protected isAllSelected(): boolean {
    return this.tableState?.isAllSelected(this.currentItems()) ?? false;
  }

  protected isIndeterminate(): boolean {
    return this.tableState?.isIndeterminate(this.currentItems()) ?? false;
  }

  protected resetSelection(): void {
    this.tableState?.clearAll();
    this.selectionChanged.emit([]);
  }
}
